import { useFollowedSite } from '../../../contexts/SiteContext';
import { Stat } from '../../../components/office';
// Wave 4B — 6 canonical operational reports on one compact file.
// Each page mounts under /back-office/reports/ops/<slug>.
import { useEffect, useMemo, useState } from 'react';
import {
  Sun, Clock, Gauge, Percent, CalendarDays, FileText, Loader2, ChevronLeft,
  ChevronRight, Download, Printer, TrendingUp, TrendingDown, Trophy,
} from 'lucide-react';
import { toast } from 'sonner';
import ReportsLayout from '../../../components/reports/ReportsLayout';
import { reportsIntelApi } from '@verlo/shared/services/api';
import SeeMoreLink from '../../../components/reports/SeeMoreLink';
import { Button } from '@verlo/shared/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@verlo/shared/components/ui/select';
import { Input } from '@verlo/shared/components/ui/input';
import { SitePicker } from '@verlo/shared/components/ui/site-picker';

const fmtMoney = (v) => v == null ? '—' : `£${Math.round(v).toLocaleString('en-GB')}`;
const fmtMoneyD = (v) => v == null ? '—' : `£${Number(v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtPct = (v) => v == null ? '—' : `${Number(v).toFixed(1)}%`;
const pad2 = (n) => String(n).padStart(2, '0');
const shiftDate = (iso, days) => {
  const d = new Date(iso);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};
const todayISO = () => new Date().toISOString().slice(0, 10);

const useSites = () => {
  const [sites, setSites] = useState([]);
  useEffect(() => {
    reportsIntelApi.listSites().then((r) => setSites(r.data.sites || [])).catch(() => {});
  }, []);
  return sites;
};

// The report's opening row: the page's name is on the top line (from `areas.js`), so this
// is the purpose line and the controls — the same shape as `office/PageHeader`.
const PageHeader = ({ subtitle, controls }) => (
  <div className="flex min-h-[36px] items-center justify-between flex-wrap gap-3 mb-5">
    <p className="text-[14px] text-ink-soft max-w-2xl">{subtitle}</p>
    <div className="flex items-center gap-2 flex-wrap">{controls}</div>
  </div>
);

const SiteSelect = ({ value, onChange, sites }) => (
  <SitePicker sites={sites} value={value} onChange={onChange} allowAll triggerClassName="h-9 w-[180px]" testId="site-select" />
);

const DateStepper = ({ value, onChange, testid }) => (
  <div className="flex items-center gap-1 bg-white border border-hairline rounded-md">
    <button onClick={() => onChange(shiftDate(value, -1))} className="h-9 w-9 flex items-center justify-center hover:bg-hover-tint" data-testid={`${testid}-prev`}>
      <ChevronLeft size={14} />
    </button>
    <Input type="date" value={value} onChange={(e) => onChange(e.target.value)} className="h-9 w-[150px] border-0 text-[12px] font-mono-display" data-testid={`${testid}-input`} />
    <button onClick={() => onChange(shiftDate(value, 1))} className="h-9 w-9 flex items-center justify-center hover:bg-hover-tint" data-testid={`${testid}-next`}>
      <ChevronRight size={14} />
    </button>
  </div>
);

const KpiTile = ({ label, value, hint, tone = 'default', seeMoreTo }) => (
  <Stat label={label} value={value} hint={hint} to={seeMoreTo || undefined}
        delta={tone === 'good' ? { text: 'On target', tone: 'ok' } : tone === 'bad' ? { text: 'Over', tone: 'stop' } : undefined} />
);

// ============================================================
// 1. Hourly Trading
// ============================================================

export const HourlyTradingPage = () => {
  const sites = useSites();
  const [siteId, setSiteId] = useFollowedSite('all');
  const [date, setDate] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    reportsIntelApi.opsHourlyTrading({ date, ...(siteId !== 'all' ? { site_id: siteId } : {}) })
      .then((r) => setData(r.data))
      .catch(() => toast.error('Could not load hourly trading'))
      .finally(() => setLoading(false));
  }, [siteId, date]);

  const buckets = data?.buckets || [];
  const maxSales = Math.max(...buckets.map((b) => b.sales), 1);

  return (
    <ReportsLayout>
      <div data-testid="hourly-trading-page">
        <PageHeader
          icon={Clock} badge="Hourly Trading"
          title="Every hour of the day, at a glance"
          subtitle="Sales, covers, AOV, labour cost, labour % and Sales-Per-Labour-Hour: one row per hour."
          controls={<>
            <SiteSelect value={siteId} onChange={setSiteId} sites={sites} />
            <DateStepper value={date} onChange={setDate} testid="hourly-date" />
          </>}
        />
        {data?.totals && (
          <div className="grid grid-cols-2 md:grid-cols-4 2xl:grid-cols-7 gap-3 mb-5">
            <KpiTile label="Sales" value={fmtMoney(data.totals.sales)} />
            <KpiTile label="Covers" value={data.totals.covers} />
            <KpiTile label="Orders" value={data.totals.orders} />
            <KpiTile label="AOV" value={fmtMoney(data.totals.aov)} />
            <KpiTile label="Labour cost" value={fmtMoney(data.totals.labour_cost)} />
            <KpiTile label="Labour %" value={fmtPct(data.totals.labour_pct)} tone={data.totals.labour_pct > 35 ? 'bad' : data.totals.labour_pct < 25 ? 'good' : 'default'} />
            <KpiTile label="SPLH" value={fmtMoney(data.totals.splh)} hint={data.peak_hour != null ? `Peak ${pad2(data.peak_hour)}:00` : null} />
          </div>
        )}
        <div className="bg-white border border-hairline/80 rounded-lg overflow-hidden" data-testid="hourly-table">
          <div className="grid grid-cols-[60px_1fr_repeat(6,90px)] px-4 py-2 border-b border-hairline text-[11px] uppercase tracking-wider font-semibold text-ink-soft bg-surface-2/60">
            <div>Hour</div><div>Sales</div><div className="text-right">Covers</div><div className="text-right">AOV</div><div className="text-right">£/cover</div><div className="text-right">Labour</div><div className="text-right">Labour %</div><div className="text-right">SPLH</div>
          </div>
          {loading && <div className="py-12 text-center text-sm text-ink-faint">Loading…</div>}
          {!loading && buckets.filter((b) => b.sales > 0 || b.labour_cost > 0).map((b) => (
            <div key={b.hour} className="grid grid-cols-[60px_1fr_repeat(6,90px)] px-4 py-2 border-t border-hairline items-center text-[12px]" data-testid={`hourly-row-${b.hour}`}>
              <div className="font-mono-display font-semibold text-ink">{pad2(b.hour)}:00</div>
              <div className="flex items-center gap-2">
                <div className="w-[140px] h-2 bg-paper rounded overflow-hidden"><div className="h-full bg-verlo" style={{ width: `${(b.sales / maxSales) * 100}%` }} /></div>
                <span className="font-mono-display font-semibold text-ink">{fmtMoney(b.sales)}</span>
              </div>
              <div className="text-right font-mono-display">{b.covers}</div>
              <div className="text-right font-mono-display">{fmtMoney(b.aov)}</div>
              <div className="text-right font-mono-display">{fmtMoney(b.avg_spend_per_cover)}</div>
              <div className="text-right font-mono-display">{fmtMoney(b.labour_cost)}</div>
              <div className={`text-right font-mono-display ${b.labour_pct > 35 ? 'text-signal-stop' : b.labour_pct > 0 && b.labour_pct < 25 ? 'text-signal-ok' : ''}`}>{fmtPct(b.labour_pct)}</div>
              <div className="text-right font-mono-display font-semibold">{fmtMoney(b.splh)}</div>
            </div>
          ))}
          {!loading && buckets.filter((b) => b.sales > 0 || b.labour_cost > 0).length === 0 && (
            <div className="py-12 text-center text-sm text-ink-faint">No trading data for this day.</div>
          )}
        </div>
      </div>
    </ReportsLayout>
  );
};

// ============================================================
// 2. SPLH
// ============================================================

export const SplhPage = () => {
  const sites = useSites();
  const [siteId, setSiteId] = useFollowedSite('all');
  const [days, setDays] = useState(14);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    reportsIntelApi.opsSplh({ days, ...(siteId !== 'all' ? { site_id: siteId } : {}) })
      .then((r) => setData(r.data)).catch(() => toast.error('Could not load SPLH'))
      .finally(() => setLoading(false));
  }, [siteId, days]);

  const series = data?.series || [];
  const maxSplh = Math.max(...series.map((s) => s.splh), 1);

  return (
    <ReportsLayout>
      <div data-testid="splh-page">
        <PageHeader
          icon={Gauge} badge="Sales Per Labour Hour"
          title="Productivity: every hour, every day"
          subtitle="How much revenue your team generates per hour of labour spent. Higher is better."
          controls={<>
            <SiteSelect value={siteId} onChange={setSiteId} sites={sites} />
            <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
              <SelectTrigger className="h-9 w-[140px]" data-testid="splh-range"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="14">Last 14 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
          </>}
        />
        {data && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
            <KpiTile label="Average SPLH" value={fmtMoney(data.avg_splh)} />
            {data.best_day && <KpiTile label="Best day" value={fmtMoney(data.best_day.splh)} hint={data.best_day.date} tone="good" />}
            {data.worst_day && <KpiTile label="Worst day" value={fmtMoney(data.worst_day.splh)} hint={data.worst_day.date} tone="bad" />}
          </div>
        )}
        <div className="bg-white border border-hairline/80 rounded-lg overflow-hidden" data-testid="splh-table">
          <div className="grid grid-cols-[110px_1fr_100px_100px_100px] px-4 py-2 border-b border-hairline text-[11px] uppercase tracking-wider font-semibold text-ink-soft bg-surface-2/60">
            <div>Date</div><div>SPLH</div><div className="text-right">Sales</div><div className="text-right">Hours</div><div className="text-right">Labour</div>
          </div>
          {loading && <div className="py-12 text-center text-sm text-ink-faint">Loading…</div>}
          {!loading && series.map((s) => (
            <div key={s.date} className="grid grid-cols-[110px_1fr_100px_100px_100px] px-4 py-2 border-t border-hairline items-center text-[12px]" data-testid={`splh-row-${s.date}`}>
              <div className="font-mono-display font-semibold text-ink">{s.date}</div>
              <div className="flex items-center gap-2">
                <div className="w-[180px] h-2 bg-paper rounded overflow-hidden"><div className="h-full bg-verlo" style={{ width: `${(s.splh / maxSplh) * 100}%` }} /></div>
                <span className="font-mono-display font-semibold text-ink">{fmtMoney(s.splh)}</span>
              </div>
              <div className="text-right font-mono-display">{fmtMoney(s.sales)}</div>
              <div className="text-right font-mono-display">{s.labour_hours.toFixed(1)}</div>
              <div className="text-right font-mono-display">{fmtMoney(s.labour_cost)}</div>
            </div>
          ))}
        </div>
      </div>
    </ReportsLayout>
  );
};

// ============================================================
// 3. Discounts & Refunds
// ============================================================

export const DiscountsRefundsPage = () => {
  const sites = useSites();
  const [siteId, setSiteId] = useFollowedSite('all');
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    reportsIntelApi.opsDiscountsRefunds({ days, ...(siteId !== 'all' ? { site_id: siteId } : {}) })
      .then((r) => setData(r.data)).catch(() => toast.error('Could not load'))
      .finally(() => setLoading(false));
  }, [siteId, days]);

  return (
    <ReportsLayout>
      <div data-testid="discounts-refunds-page">
        <PageHeader
          icon={Percent} badge="Discounts & Refunds"
          title="Where trade is leaking"
          subtitle="Every discount and refund, by reason, by manager, by day. Repeat-offender flag included."
          controls={<>
            <SiteSelect value={siteId} onChange={setSiteId} sites={sites} />
            <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
              <SelectTrigger className="h-9 w-[140px]" data-testid="dr-range"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
          </>}
        />
        {data && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <KpiTile label="Discounts" value={fmtMoney(data.total_discount)} tone={data.total_discount > 500 ? 'bad' : 'default'} />
            <KpiTile label="Refunds" value={fmtMoney(data.total_refund)} tone={data.total_refund > 500 ? 'bad' : 'default'} />
            <KpiTile label="Total cost" value={fmtMoney(data.total_cost)} tone="bad" />
            <KpiTile label="% of sales" value={fmtPct(data.cost_pct_of_sales)} />
          </div>
        )}
        <div className="grid lg:grid-cols-2 gap-4">
          <div className="bg-white border border-hairline rounded-lg overflow-hidden" data-testid="dr-by-reason">
            <div className="px-5 py-3 border-b border-hairline">
              <h3 className="text-sm font-semibold text-ink">By reason</h3>
            </div>
            <div className="divide-y divide-hairline">
              {(data?.reasons || []).map((r) => (
                <div key={r.reason} className="flex items-center justify-between px-5 py-2 text-[12px]" data-testid={`dr-reason-${r.reason}`}>
                  <span className="text-ink-soft">{r.reason}</span>
                  <span className="font-mono-display font-semibold text-ink">{fmtMoney(r.amount)}</span>
                </div>
              ))}
              {(data?.reasons || []).length === 0 && !loading && <div className="px-5 py-6 text-center text-sm text-ink-faint">No discounts/refunds in this window.</div>}
            </div>
          </div>
          <div className="bg-white border border-hairline rounded-lg overflow-hidden" data-testid="dr-by-manager">
            <div className="px-5 py-3 border-b border-hairline">
              <h3 className="text-sm font-semibold text-ink">By manager / server</h3>
            </div>
            <div className="divide-y divide-hairline">
              {(data?.managers || []).map((m) => (
                <div key={m.id} className="flex items-center justify-between gap-3 px-5 py-2 text-[12px]" data-testid={`dr-manager-${m.id}`}>
                  <div className="flex items-baseline gap-2">
                    <span className="text-ink-soft">{m.name}</span>
                    {m.repeat_offender && <span className="text-[11px] uppercase tracking-wider font-semibold text-signal-stop bg-signal-stop-tint px-1.5 py-0.5 rounded">Repeat</span>}
                  </div>
                  <div className="flex items-baseline gap-3">
                    <span className="text-[12.5px] text-ink-soft">{m.count} events</span>
                    <span className="font-mono-display font-semibold text-ink w-16 text-right">{fmtMoney(m.amount)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          {/* Which dishes come back, from refunds made by ticking dishes on the till. */}
          {(data?.dishes || []).length > 0 && (
            <div className="bg-white border border-hairline rounded-lg overflow-hidden" data-testid="dr-by-dish">
              <div className="px-5 py-3 border-b border-hairline">
                <h3 className="text-sm font-semibold text-ink">Refunded dishes</h3>
              </div>
              <div className="divide-y divide-hairline">
                {data.dishes.map((d) => (
                  <div key={d.name} className="flex items-center justify-between gap-3 px-5 py-2 text-[12px]">
                    <span className="text-ink-soft">{d.name}</span>
                    <div className="flex items-baseline gap-3">
                      <span className="text-[12.5px] text-ink-soft">{d.quantity} refunded</span>
                      <span className="font-mono-display font-semibold text-ink w-16 text-right">{fmtMoney(d.amount)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </ReportsLayout>
  );
};

// ============================================================
// 4. Daily Trading
// ============================================================

export const DailyTradingPage = () => {
  const sites = useSites();
  const [siteId, setSiteId] = useFollowedSite('all');
  const [date, setDate] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    reportsIntelApi.opsDailyTrading({ date, ...(siteId !== 'all' ? { site_id: siteId } : {}) })
      .then((r) => setData(r.data)).catch(() => toast.error('Could not load'))
      .finally(() => setLoading(false));
  }, [siteId, date]);

  return (
    <ReportsLayout>
      <div data-testid="daily-trading-page">
        <PageHeader
          icon={Sun} badge="Daily Trading Report"
          title="Close of day, in one page"
          subtitle="Revenue, covers, labour, top sellers and variance to yesterday. Printable, board-ready."
          controls={<>
            <SiteSelect value={siteId} onChange={setSiteId} sites={sites} />
            <DateStepper value={date} onChange={setDate} testid="daily-date" />
            <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={() => window.print()} data-testid="daily-print">
              <Printer size={13} /> Print
            </Button>
          </>}
        />
        {data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
              <KpiTile label="Sales" value={fmtMoney(data.today.sales)} hint={data.delta.pct != null ? `${data.delta.pct >= 0 ? '+' : ''}${data.delta.pct}% vs yesterday` : null} tone={data.delta.pct > 0 ? 'good' : data.delta.pct < 0 ? 'bad' : 'default'} />
              <KpiTile label="Covers" value={data.today.covers} />
              <KpiTile label="AOV" value={fmtMoney(data.today.aov)} />
              <KpiTile label="£/cover" value={fmtMoney(data.today.avg_spend_per_cover)} />
              <KpiTile label="Labour cost" value={fmtMoney(data.labour.cost)} />
              <KpiTile label="Labour hours" value={data.labour.hours.toFixed(1)} />
              <KpiTile label="Labour %" value={fmtPct(data.labour.pct_of_sales)} tone={data.labour.pct_of_sales > 35 ? 'bad' : 'default'} />
              <KpiTile label="SPLH" value={fmtMoney(data.labour.splh)} />
            </div>
            <div className="bg-white border border-hairline rounded-lg overflow-hidden" data-testid="daily-top-sellers">
              <div className="px-5 py-3 border-b border-hairline"><h3 className="text-sm font-semibold text-ink">Top 5 sellers today</h3></div>
              <div className="divide-y divide-hairline">
                {data.top_sellers.map((t, i) => (
                  <div key={i} className="flex items-baseline justify-between px-5 py-2 text-[12px]">
                    <span className="text-ink-soft"><span className="text-ink-faint font-mono-display mr-2">#{i + 1}</span>{t.name}</span>
                    <div className="flex items-baseline gap-3">
                      <span className="text-[12.5px] text-ink-soft">{t.qty} sold</span>
                      <span className="font-mono-display font-semibold text-ink w-20 text-right">{fmtMoney(t.revenue)}</span>
                    </div>
                  </div>
                ))}
                {data.top_sellers.length === 0 && !loading && <div className="px-5 py-6 text-center text-sm text-ink-faint">No sales today.</div>}
              </div>
            </div>
          </>
        )}
      </div>
    </ReportsLayout>
  );
};

// ============================================================
// 5. Weekly Performance
// ============================================================

export const WeeklyPerformancePage = () => {
  const sites = useSites();
  const [siteId, setSiteId] = useFollowedSite('all');
  const [weekStart, setWeekStart] = useState(() => {
    const d = new Date();
    d.setUTCDate(d.getUTCDate() - d.getUTCDay());
    return d.toISOString().slice(0, 10);
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    reportsIntelApi.opsWeeklyPerformance({ week_start: weekStart, ...(siteId !== 'all' ? { site_id: siteId } : {}) })
      .then((r) => setData(r.data)).catch(() => toast.error('Could not load'))
      .finally(() => setLoading(false));
  }, [siteId, weekStart]);

  return (
    <ReportsLayout>
      <div data-testid="weekly-performance-page">
        <PageHeader
          icon={CalendarDays} badge="Weekly Performance"
          title="Seven days, side by side"
          subtitle="Sales, covers, labour and SPLH per day, with year-over-year comparison."
          controls={<>
            <SiteSelect value={siteId} onChange={setSiteId} sites={sites} />
            <DateStepper value={weekStart} onChange={(v) => setWeekStart(v)} testid="weekly-date" />
          </>}
        />
        {data?.totals && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
            <KpiTile label="Week sales" value={fmtMoney(data.totals.sales)} hint={data.totals.vs_ly_pct != null ? `${data.totals.vs_ly_pct >= 0 ? '+' : ''}${data.totals.vs_ly_pct}% vs LY` : null} tone={data.totals.vs_ly_pct > 0 ? 'good' : data.totals.vs_ly_pct < 0 ? 'bad' : 'default'} />
            <KpiTile label="Covers" value={data.totals.covers} />
            <KpiTile label="AOV" value={fmtMoney(data.totals.aov)} />
            <KpiTile label="Labour %" value={fmtPct(data.totals.labour_pct)} tone={data.totals.labour_pct > 35 ? 'bad' : 'default'} />
            <KpiTile label="SPLH" value={fmtMoney(data.totals.splh)} />
          </div>
        )}
        <div className="bg-white border border-hairline rounded-lg overflow-hidden" data-testid="weekly-table">
          <div className="grid grid-cols-[80px_1fr_repeat(6,90px)] px-4 py-2 border-b border-hairline text-[11px] uppercase tracking-wider font-semibold text-ink-soft bg-surface-2/60">
            <div>Day</div><div>Sales</div><div className="text-right">Covers</div><div className="text-right">AOV</div><div className="text-right">Labour</div><div className="text-right">Labour %</div><div className="text-right">SPLH</div><div className="text-right">vs LY</div>
          </div>
          {loading && <div className="py-12 text-center text-sm text-ink-faint">Loading…</div>}
          {!loading && (data?.days || []).map((d) => (
            <div key={d.date} className="grid grid-cols-[80px_1fr_repeat(6,90px)] px-4 py-2 border-t border-hairline items-center text-[12px]" data-testid={`weekly-row-${d.date}`}>
              <div><div className="font-mono-display font-semibold text-ink">{d.day_name}</div><div className="text-[12.5px] text-ink-faint font-mono-display">{d.date.slice(5)}</div></div>
              <div className="font-mono-display font-semibold text-ink">{fmtMoney(d.sales)}</div>
              <div className="text-right font-mono-display">{d.covers}</div>
              <div className="text-right font-mono-display">{fmtMoney(d.sales && d.orders ? d.sales / d.orders : 0)}</div>
              <div className="text-right font-mono-display">{fmtMoney(d.labour_cost)}</div>
              <div className={`text-right font-mono-display ${d.labour_pct > 35 ? 'text-signal-stop' : ''}`}>{fmtPct(d.labour_pct)}</div>
              <div className="text-right font-mono-display font-semibold">{fmtMoney(d.splh)}</div>
              <div className={`text-right font-mono-display ${d.vs_ly_pct == null ? 'text-ink-faint' : d.vs_ly_pct >= 0 ? 'text-signal-ok' : 'text-signal-stop'}`}>
                {d.vs_ly_pct == null ? '—' : `${d.vs_ly_pct >= 0 ? '+' : ''}${d.vs_ly_pct}%`}
              </div>
            </div>
          ))}
        </div>
      </div>
    </ReportsLayout>
  );
};

// ============================================================
// 6. Monthly Board Pack
// ============================================================

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export const MonthlyBoardPackPage = () => {
  const sites = useSites();
  const now = new Date();
  const [siteId, setSiteId] = useFollowedSite('all');
  const [year, setYear] = useState(now.getUTCFullYear());
  const [month, setMonth] = useState(now.getUTCMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    reportsIntelApi.opsMonthlyBoardPack({ year, month, ...(siteId !== 'all' ? { site_id: siteId } : {}) })
      .then((r) => setData(r.data)).catch(() => toast.error('Could not load'))
      .finally(() => setLoading(false));
  }, [siteId, year, month]);

  // The pack for the board is a PDF, made by the server (D3): Print printed the back office too.
  const [downloading, setDownloading] = useState(false);
  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const r = await reportsIntelApi.opsMonthlyBoardPackPdf({ year, month, ...(siteId !== 'all' ? { site_id: siteId } : {}) });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `board-pack-${year}-${pad2(month)}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      toast.error("Couldn't make the PDF");
    } finally { setDownloading(false); }
  };

  const step = (delta) => {
    let m = month + delta, y = year;
    while (m < 1) { m += 12; y -= 1; }
    while (m > 12) { m -= 12; y += 1; }
    setMonth(m); setYear(y);
  };

  return (
    <ReportsLayout>
      <div data-testid="monthly-board-pack-page">
        <PageHeader
          icon={FileText} badge="Monthly Board Pack"
          title="The month in one page"
          subtitle="The month's trading and labour, week by week: ready to hand to the board."
          controls={<>
            <SiteSelect value={siteId} onChange={setSiteId} sites={sites} />
            <div className="flex items-center gap-1 bg-white border border-hairline rounded-md">
              <button onClick={() => step(-1)} className="h-9 w-9 flex items-center justify-center hover:bg-hover-tint" data-testid="monthly-prev"><ChevronLeft size={14} /></button>
              <span className="px-3 text-[13px] font-semibold font-mono-display" data-testid="monthly-period">{MONTHS[month - 1]} {year}</span>
              <button onClick={() => step(1)} className="h-9 w-9 flex items-center justify-center hover:bg-hover-tint" data-testid="monthly-next"><ChevronRight size={14} /></button>
            </div>
            <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={downloadPdf} disabled={downloading || !data?.current} data-testid="monthly-pdf">
              {downloading ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} Download PDF
            </Button>
          </>}
        />
        {data?.current && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
              <KpiTile label="Sales" value={fmtMoney(data.current.sales)} hint={data.delta.sales_pct != null ? `${data.delta.sales_pct >= 0 ? '+' : ''}${data.delta.sales_pct}% vs ${data.previous?.to_date ? 'same days last month' : 'last month'}` : null} tone={data.delta.sales_pct > 0 ? 'good' : data.delta.sales_pct < 0 ? 'bad' : 'default'} />
              <KpiTile label="Covers" value={(data.current.covers ?? 0).toLocaleString('en-GB')} />
              <KpiTile label="AOV" value={fmtMoney(data.current.aov)} />
              <KpiTile label="Labour %" value={fmtPct(data.current.labour_pct)} tone={data.current.labour_pct > 35 ? 'bad' : 'default'} />
              <KpiTile label="SPLH" value={fmtMoney(data.current.splh)} />
            </div>
            <div className="bg-white border border-hairline rounded-lg overflow-hidden" data-testid="monthly-weekly-rollup">
              <div className="px-5 py-3 border-b border-hairline"><h3 className="text-sm font-semibold text-ink">Weekly rollup</h3></div>
              <div className="divide-y divide-hairline">
                {(data.weekly || []).map((w) => (
                  <div key={w.week} className="grid grid-cols-[80px_1fr_100px_100px] px-5 py-2 items-center text-[12px]">
                    <div className="text-[11px] uppercase tracking-wider font-semibold text-ink-soft">Week {w.week}</div>
                    <div className="w-full h-2 bg-paper rounded overflow-hidden">
                      <div className="h-full bg-verlo" style={{ width: `${(w.sales / (data.current.sales || 1)) * 100}%` }} />
                    </div>
                    <div className="text-right font-mono-display">{w.covers} covers</div>
                    <div className="text-right font-mono-display font-semibold">{fmtMoney(w.sales)}</div>
                  </div>
                ))}
                {(data.weekly || []).length === 0 && !loading && <div className="px-5 py-6 text-center text-sm text-ink-faint">No trading data for this month.</div>}
              </div>
            </div>
          </>
        )}
      </div>
    </ReportsLayout>
  );
};
